import { Button, SelectInput, showToast, type SelectInputOption } from '@my/ui';
import { FileSpreadsheet, Loader2, Upload } from 'lucide-react';
import { useRef, useState, type DragEvent } from 'react';
import { parseImportFile } from '../lib/parseImportFile';
import { IMPORT_MAX_ROWS, type ParsedFile } from '../lib/types';

interface UploadStepProps {
  /** Current parsed file (persisted on the wizard so re-entering the step keeps it). */
  parsed: ParsedFile | null;
  /** Lifts a successful parse (+ the source File, for sheet switching) to the wizard. */
  onParsed: (parsed: ParsedFile, file: File) => void;
  /** The source File the wizard is holding (so a sheet switch can re-parse it). */
  file: File | null;
  /** Advance to the mapping step. */
  onContinue: () => void;
  /** Optional — shows a Back button for wizards where upload isn't the first
   *  step (e.g. Resources, which picks a resource type first). */
  onBack?: () => void;
}

/**
 * Step 1 — drop / browse a JSON, CSV or Excel file. Parses it client-side
 * (no upload to the server — onboarding data only leaves the browser at the
 * commit step, per-row). Shows a quick read-out (format · row count · the
 * detected columns) so the operator can sanity-check before mapping, plus a
 * sheet picker for multi-tab workbooks.
 */
export function UploadStep({ parsed, onParsed, file, onContinue, onBack }: UploadStepProps) {
  const [dragActive, setDragActive] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFile = async (next: File | null) => {
    if (!next) return;
    setParsing(true);
    setError(null);
    try {
      const result = await parseImportFile(next);
      if (result.rows.length === 0) {
        setError('No rows found in the file.');
        return;
      }
      if (result.rows.length > IMPORT_MAX_ROWS) {
        setError(
          `That file has ${result.rows.length.toLocaleString()} rows — the importer handles up to ` +
            `${IMPORT_MAX_ROWS.toLocaleString()} at a time. Split it and import in batches.`,
        );
        return;
      }
      onParsed(result, next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read that file.');
    } finally {
      setParsing(false);
    }
  };

  // Sheet switch (xlsx) — re-parse the held File against the chosen tab.
  const handleSheetChange = async (sheet: string) => {
    if (!file) return;
    setParsing(true);
    setError(null);
    try {
      const result = await parseImportFile(file, sheet);
      onParsed(result, file);
    } catch (err) {
      showToast({ type: 'error', message: err instanceof Error ? err.message : 'Sheet read failed.' });
    } finally {
      setParsing(false);
    }
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };
  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    void handleFile(e.dataTransfer.files?.[0] ?? null);
  };

  const sheetOptions: SelectInputOption[] =
    parsed?.sheetNames?.map((s) => ({ value: s, label: s })) ?? [];

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={
          'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-8 text-center transition-colors ' +
          (dragActive
            ? 'border-blue-500 bg-blue-50'
            : 'border-slate-300 bg-slate-50 hover:border-slate-400')
        }
      >
        {parsing ? (
          <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
        ) : (
          <Upload className="h-6 w-6 text-slate-500" aria-hidden="true" />
        )}
        <p className="text-sm font-medium text-slate-700">
          Drop a file here, or click to browse
        </p>
        <p className="text-xs text-slate-500">JSON, CSV, or Excel (.xlsx / .xls) — up to {IMPORT_MAX_ROWS.toLocaleString()} rows</p>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".json,.csv,.xlsx,.xls"
        className="hidden"
        onChange={(e) => {
          void handleFile(e.target.files?.[0] ?? null);
          e.target.value = '';
        }}
      />

      {error && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </p>
      )}

      {/* Parsed read-out */}
      {parsed && (
        <div className="space-y-3 rounded-md border border-slate-200 bg-white p-3">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4 text-slate-400" />
            <span className="text-sm font-semibold text-slate-900">{parsed.fileName}</span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase text-slate-600">
              {parsed.format}
            </span>
            <span className="text-xs text-slate-500">
              {parsed.rows.length.toLocaleString()} row{parsed.rows.length === 1 ? '' : 's'} ·{' '}
              {parsed.headers.length} column{parsed.headers.length === 1 ? '' : 's'}
            </span>
          </div>

          {parsed.sheetNames && parsed.sheetNames.length > 1 && (
            <div className="max-w-xs">
              <SelectInput
                label="Sheet"
                name="import-sheet"
                value={parsed.activeSheet ?? ''}
                options={sheetOptions}
                disabled={parsing}
                onChange={(e) => void handleSheetChange(e.target.value)}
              />
            </div>
          )}

          <div className="flex flex-wrap gap-1.5">
            {parsed.headers.map((h) => (
              <span
                key={h}
                className="inline-flex items-center rounded-sm border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-600"
              >
                {h}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className={onBack ? 'flex items-center justify-between gap-3' : 'flex justify-end'}>
        {onBack && (
          <Button type="button" variant="ghost" onClick={onBack} disabled={parsing}>
            Back
          </Button>
        )}
        <Button type="button" onClick={onContinue} disabled={!parsed || parsing}>
          Continue
        </Button>
      </div>
    </div>
  );
}
